/* eslint-disable react/jsx-no-undef */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import CardLayout from "../Cards/CardLayout";
import ReactECharts from "echarts-for-react";
import { useFetchData } from "../../customHooks/useGetDashboardData";
import Loader from "../feedBacks/loader";
import CardError from "../feedBacks/CardError";
import NoContent from "../feedBacks/NoContent";
import { COLORS, REFETCH_CONFIGURATION } from "../../utils/constants";
import { EChartsOption } from "echarts-for-react";
import { useEffect, useState } from "react";
import NoDeviceCardError from "../feedBacks/NoDeviceCardError";
import { useUtils } from "../../customHooks/useUtils";

interface IDailyConsumption {
  date: string;
  consumption: number;
}

function DailyConsumptionChart({
  site_id,
  startDate,
  endDate,
}: {
  site_id: string;
  startDate: string;
  endDate: string;
}) {
  const { FEEDBACK_MESSAGES, t } = useUtils();
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const {
    data: dailyConsumptionData,
    isLoading: isDataLoading,
    error,
    refetch,
  } = useFetchData(
    ["/finance", "fetchDailyConsumption"],
    `/finance/daily-consumption/${site_id}`,
    {
      start_date: startDate,
      end_date: endDate,
    },
    // @ts-ignore
    {
      ...REFETCH_CONFIGURATION,
    }
  );

  // Transform API response into chart-friendly data
  const transformedData = dailyConsumptionData
    ? (dailyConsumptionData as IDailyConsumption[]).map((item) => ({
        title: item.date,
        consumption: item.consumption,
      }))
    : [];

  const isLoading = isDataLoading || isLocalLoading;

  useEffect(() => {
    if (startDate && endDate) {
      setIsLocalLoading(true);
      refetch().finally(() => {
        setIsLocalLoading(false);
      });
    }
  }, [startDate, endDate, refetch]);

  const chartData = transformedData.map((item) => item.consumption);
  const chartLabels = transformedData.map((item) => item.title);
  const maxVal = Math.max(...chartData, 0);

  const options: EChartsOption = {
    grid: {
      left: "5%",
      right: "5%",
      top: "10%",
      bottom: "15%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: chartLabels.length > 0 ? chartLabels : [FEEDBACK_MESSAGES.NO_DATA],
      axisLabel: {
        fontWeight: "bold",
        fontSize: 11,
        interval: 0,
        rotate: 45,
        overflow: "truncate",
      },
    },
    yAxis: {
      type: "value",
      max: "dataMax",
      axisLabel: {
        fontWeight: "bold",
        fontSize: 11,
      },
    },
    series: [
      {
        type: "bar",
        data: chartData.map(() => maxVal),
        barGap: "-100%",
        barWidth: "40%",
        itemStyle: {
          color: "#e0e0e0",
          borderRadius: [8, 8, 8, 8],
        },
        silent: true,
      },
      {
        name: t("chart.daily_consumption"),
        type: "bar",
        data: chartData,
        barWidth: "40%",
        itemStyle: {
          color: COLORS.PRIMARY || "#FFD700",
          borderRadius: [8, 8, 8, 8],
        },
        z: 10,
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: function (params: any) {
        const data = params[1];
        return `<div style="font-size:10px">${data.value.toLocaleString()} kWh</div>`;
      },
      textStyle: {
        fontSize: 10,
      },
    },
    animationDuration: 0,
    animationDurationUpdate: 3000,
    animationEasing: "linear",
    animationEasingUpdate: "linear",
  };

  return (
    <CardLayout
      title={t("chart.daily_consumption")}
      className="lg:min-w-[570px] flex-1"
    >
      <div className="flex flex-col h-full w-full">
        {isLoading ? (
          <div className="flex justify-center items-center h-full w-full">
            <Loader />
          </div>
        ) : error ? (
          (error as any).response?.data?.detail ? (
            <NoDeviceCardError
              message={FEEDBACK_MESSAGES.NO_DEVICE}
              className="!max-h-[150px]"
            />
          ) : (
            <CardError
              message={FEEDBACK_MESSAGES.ERROR}
              className="!h-[100px]"
            />
          )
        ) : transformedData.length === 0 ? (
          <NoContent message={FEEDBACK_MESSAGES.NO_DATA} />
        ) : (
          <div className="h-full w-full relative p-0">
            <ReactECharts option={options} />
          </div>
        )}
      </div>
    </CardLayout>
  );
}

export default DailyConsumptionChart;
